<script setup>
import { ref, watch, nextTick } from 'vue'
import L from 'leaflet'
import { useNominatim } from '@/composables/useNominatim'
import { useSavedPlaces } from '@/composables/useSavedPlaces'
import { useMapTiles } from '@/composables/useMapTiles'
import MapLayerControls from '@/components/MapLayerControls.vue'
import { DEFAULT_CENTER } from '@/store/mapConstants'

const props = defineProps({
  visible: Boolean,
  userLatLng: { type: Array, default: null },
})
const emit = defineEmits(['update:visible'])

const { reverseGeocode } = useNominatim()
const { places, add, remove } = useSavedPlaces()

// ─── Mapa ────────────────────────────────────────────────────────────────────
const mapContainer = ref(null)
let leafletMap = null
let marker = null
let userDotMarker = null

const { applyCurrentTiles, isSatellite, googleEnabled, toggle: toggleMapLayer, toggleGoogle } = useMapTiles(() => leafletMap)

const selectedLatlng = ref(null)
const selfLatLng = ref(null) // GPS propio: viene de prop o del API nativa
const suggestedName = ref('')
const customName = ref('')
const geocoding = ref(false)
const saving = ref(false)

const addUserDot = (lat, lng) => {
  if (!leafletMap) return
  const ll = [lat, lng]
  if (userDotMarker) {
    userDotMarker.setLatLng(ll)
  } else {
    userDotMarker = L.marker(ll, {
      interactive: false,
      zIndexOffset: -100,
      icon: L.divIcon({
        className: '',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        html: '<div style="width:16px;height:16px;border-radius:50%;background:#007aff;border:2.5px solid #fff;box-shadow:0 0 0 4px rgba(0,122,255,0.22)"></div>',
      }),
    }).addTo(leafletMap)
  }
}

const initMap = async () => {
  await nextTick()
  if (!mapContainer.value || leafletMap) return

  // Usar ubicación del prop si está disponible; si no, coordenada por defecto
  const knownLatLng = props.userLatLng
  const center = knownLatLng ? [knownLatLng[0], knownLatLng[1]] : DEFAULT_CENTER

  leafletMap = L.map(mapContainer.value, {
    scrollWheelZoom: false,
    zoomControl: true,
  }).setView(center, knownLatLng ? 16 : 14)

  applyCurrentTiles(leafletMap)

  leafletMap.on('click', async (e) => {
    placeMarker(e.latlng.lat, e.latlng.lng)
    await reverseAt(e.latlng.lat, e.latlng.lng)
  })

  if (knownLatLng) {
    // Ya tenemos GPS del padre — mostrar dot y centrar
    selfLatLng.value = { lat: knownLatLng[0], lng: knownLatLng[1] }
    addUserDot(knownLatLng[0], knownLatLng[1])
  } else if (navigator.geolocation) {
    // Pedir GPS directamente al navegador
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude
        selfLatLng.value = { lat, lng }
        if (leafletMap) {
          leafletMap.setView([lat, lng], 16)
          addUserDot(lat, lng)
        }
      },
      () => { /* sin permiso: dejar el mapa donde está */ },
      { timeout: 8000, maximumAge: 30000 }
    )
  }
}

const destroyMap = () => {
  if (leafletMap) {
    leafletMap.remove()
    leafletMap = null
    marker = null
    userDotMarker = null
  }
  selectedLatlng.value = null
  selfLatLng.value = null
  suggestedName.value = ''
  customName.value = ''
}

const placeMarker = (lat, lng) => {
  selectedLatlng.value = { lat, lng }
  if (marker) {
    marker.setLatLng([lat, lng])
  } else {
    marker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: '',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        html: '<div style="width:24px;height:24px;border-radius:50%;background:#007aff;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>',
      }),
    }).addTo(leafletMap)
  }
}

const reverseAt = async (lat, lng) => {
  geocoding.value = true
  try {
    const data = await reverseGeocode({ lat, lng })
    if (data?.address) {
      const a = data.address
      const name = data.intersection
        || [a.road, a.house_number].filter(Boolean).join(' ')
        || data.display_name?.split(',')[0]
        || ''
      suggestedName.value = name
      if (!customName.value) customName.value = name
    }
  } catch { /* silencioso */ } finally {
    geocoding.value = false
  }
}

// ─── Usar ubicación actual ────────────────────────────────────────────────────
const useCurrentLocation = async () => {
  const ll = selfLatLng.value
  if (!ll) return
  placeMarker(ll.lat, ll.lng)
  leafletMap?.setView([ll.lat, ll.lng], 16)
  await reverseAt(ll.lat, ll.lng)
}

// Mantener el dot actualizado si el padre empieza a trackear después de abrir
watch(() => props.userLatLng, (ll) => {
  if (!ll) return
  selfLatLng.value = { lat: ll[0], lng: ll[1] }
  addUserDot(ll[0], ll[1])
})

// ─── Guardar lugar ────────────────────────────────────────────────────────────
const saveLugar = () => {
  const name = customName.value.trim()
  if (!name || !selectedLatlng.value) return
  add(name, selectedLatlng.value.lat, selectedLatlng.value.lng)
  // resetear selección pero mantener el mapa
  selectedLatlng.value = null
  suggestedName.value = ''
  customName.value = ''
  if (marker) { marker.remove(); marker = null }
}

// ─── Ciclo de vida ────────────────────────────────────────────────────────────
watch(() => props.visible, async (val) => {
  if (val) {
    await nextTick()
    initMap()
  } else {
    destroyMap()
  }
})
</script>

<template>
  <Transition name="mislugares-overlay" :duration="{ enter: 350, leave: 300 }">
    <div v-if="visible" class="ml-overlay">
      <div class="ml-backdrop" @click="emit('update:visible', false)" />
      <div class="ml-sheet">
        <div class="drag-handle" />

        <div class="ml-header">
          <h2 class="ml-title">Mis lugares</h2>
          <button class="ml-close" @click="emit('update:visible', false)">
            <i class="pi pi-times" />
          </button>
        </div>

        <!-- Mapa para agregar lugar -->
        <div class="add-section">
          <div class="add-section-title">Agregar lugar</div>

          <div class="place-map-wrap">
            <div ref="mapContainer" class="place-map" />
            <MapLayerControls
              :isSatellite="isSatellite"
              :google="googleEnabled"
              @toggle-satellite="toggleMapLayer"
              @toggle-google="toggleGoogle"
            />
          </div>

          <div class="map-hint">
            <i class="pi pi-hand-pointer hint-icon" />
            <span>Tocá el mapa para marcar un punto</span>
            <button
              v-if="selfLatLng"
              class="btn-mi-ubicacion"
              @click="useCurrentLocation"
            >
              <i class="pi pi-map-marker" />
              Mi ubicación
            </button>
          </div>

          <div v-if="selectedLatlng" class="name-section">
            <div v-if="geocoding" class="geocoding-hint">
              <i class="pi pi-spin pi-spinner" />
              Obteniendo dirección...
            </div>
            <div v-else-if="suggestedName" class="geocoding-hint">
              <i class="pi pi-map-marker" style="color:var(--accent)" />
              {{ suggestedName }}
            </div>
            <input
              v-model="customName"
              class="name-input"
              type="text"
              placeholder="Nombre del lugar (ej: Alojamiento)"
              enterkeyhint="done"
              @keyup.enter="saveLugar"
            />
            <button
              class="btn-save"
              :disabled="!customName.trim()"
              @click="saveLugar"
            >
              <i class="pi pi-check" />
              Guardar lugar
            </button>
          </div>
        </div>

        <!-- Lista de lugares guardados -->
        <div v-if="places.length" class="saved-section">
          <div class="saved-section-title">Guardados</div>
          <div class="saved-list">
            <div v-for="(p, idx) in places" :key="idx" class="saved-row">
              <span class="saved-icon"><i class="pi pi-home" /></span>
              <span class="saved-name">{{ p.name }}</span>
              <button class="btn-delete" @click="remove(idx)">
                <i class="pi pi-trash" />
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="!selectedLatlng" class="empty-hint">
          Tocá el mapa para guardar tu primer lugar
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.ml-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
}

.ml-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.ml-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-grouped);
  border-radius: 20px 20px 0 0;
  padding: 8px 0;
  padding-bottom: max(env(safe-area-inset-bottom, 16px), 16px);
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
}

.drag-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--label-rgb), 0.25);
  margin: 4px auto 10px;
}

.ml-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 12px;
}

.ml-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--label-primary);
  margin: 0;
  letter-spacing: -0.3px;
}

.ml-close {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(var(--label-rgb), 0.12);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--label-rgb), 0.6);
  font-size: 0.75rem;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.ml-close::after {
  content: '';
  position: absolute;
  inset: -8px;
}

/* Sección agregar */
.add-section {
  background: var(--bg-primary);
  margin: 0 16px 14px;
  border-radius: 14px;
  overflow: hidden;
}

.add-section-title,
.saved-section-title {
  font-size: 11px;
  font-weight: 600;
  color: rgba(var(--label-rgb), 0.5);
  letter-spacing: 0.5px;
  padding: 10px 14px 6px;
}

.place-map-wrap {
  position: relative;
}

.place-map {
  height: 220px;
  width: 100%;
  display: block;
}

.map-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 12px;
  color: rgba(var(--label-rgb), 0.5);
  border-top: 1px solid rgba(var(--label-rgb), 0.08);
}

.hint-icon {
  font-size: 0.8rem;
  flex-shrink: 0;
}

.btn-mi-ubicacion {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
}

.name-section {
  padding: 10px 14px 14px;
  border-top: 1px solid rgba(var(--label-rgb), 0.08);
}

.geocoding-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(var(--label-rgb), 0.55);
  margin-bottom: 8px;
}

.name-input {
  display: block;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--bg-grouped);
  border: none;
  font-size: 15px;
  color: var(--label-primary);
  outline: none;
  margin-bottom: 10px;
  box-sizing: border-box;
  -webkit-appearance: none;
}

.name-input::placeholder {
  color: rgba(var(--label-rgb), 0.35);
}

.btn-save {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 13px;
  background: var(--success);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.12s, transform 0.08s;
}

.btn-save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-save:not(:disabled):active {
  transform: scale(0.97);
}

/* Lista guardados */
.saved-section {
  background: var(--bg-primary);
  margin: 0 16px;
  border-radius: 14px;
  overflow: hidden;
}

.saved-list {
  /* sin padding propio, las filas tienen el suyo */
}

.saved-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  border-bottom: 1px solid rgba(var(--label-rgb), 0.1);
}

.saved-row:last-child {
  border-bottom: none;
}

.saved-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(var(--label-rgb), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--label-rgb), 0.5);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.saved-name {
  flex: 1;
  font-size: 15px;
  color: var(--label-primary);
}

.btn-delete {
  background: none;
  border: none;
  color: rgba(var(--label-rgb), 0.35);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 4px;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.btn-delete::after {
  content: '';
  position: absolute;
  inset: -8px;
}

.btn-delete:active {
  color: var(--danger);
}

.empty-hint {
  text-align: center;
  font-size: 13px;
  color: rgba(var(--label-rgb), 0.4);
  padding: 12px 16px 4px;
}

/* Transición */
.mislugares-overlay-enter-active .ml-sheet,
.mislugares-overlay-leave-active .ml-sheet {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.mislugares-overlay-enter-active .ml-backdrop,
.mislugares-overlay-leave-active .ml-backdrop {
  transition: opacity 0.3s ease;
}

.mislugares-overlay-enter-from .ml-sheet,
.mislugares-overlay-leave-to .ml-sheet {
  transform: translateY(100%);
}

.mislugares-overlay-enter-from .ml-backdrop,
.mislugares-overlay-leave-to .ml-backdrop {
  opacity: 0;
}
</style>
